import React, { useState } from "react";
import { Phone, Mail, MapPin, Heart, Instagram, Linkedin, Facebook } from "lucide-react";
import emailjs from "@emailjs/browser";
import toast from "react-hot-toast";

const SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const Footer = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          form_type: "Contact Form",
          name: form.name,
          email: form.email,
          phone: form.phone,
          position: "—",
          message: form.message,
          time: new Date().toLocaleString(),
        },
        PUBLIC_KEY
      );

      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      console.error("Contact form error:", err);
      const msg = err?.text || err?.message || "Failed to send your message. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-white py-16 px-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
        {/* LEFT SIDE: CONTACT INFO + DONATION */}
        <div>
          <h2 className="text-2xl font-semibold mb-6 text-brand">
            Al Noor EdTech (Pvt.) Limited
          </h2>

          <div className="space-y-4 text-gray-300 mb-10">
            <div className="flex items-start space-x-3">
              <Phone className="w-5 h-5 text-brand mt-1" />
              <p>+92 334 0929349</p>
            </div>
            <div className="flex items-start space-x-3">
              <Mail className="w-5 h-5 text-brand mt-1" />
              <p>alnoor.edtech@gmail.com</p>
            </div>
            <div className="flex items-start space-x-3">
              <MapPin className="w-5 h-5 text-brand mt-1" />
              <p>Pakistan</p>
            </div>
          </div>

          {/* DONATION */}
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-brand flex items-center gap-2">
              <Heart className="text-brand w-8 h-8" /> Support Our Mission
            </h2>
            <p className="text-gray-300 mb-6 text-sm leading-relaxed">
              Your donation helps us provide quality education, build learning
              tools, and empower students across Pakistan. Every contribution
              makes a difference!
            </p>
            <button
              disabled
              title="Donation portal coming soon"
              className="flex items-center justify-center gap-2 bg-gray-700 text-gray-300 font-semibold px-6 py-2 rounded-md cursor-not-allowed w-full sm:w-auto"
            >
              <Heart className="w-4 h-4" /> Donate (Coming Soon)
            </button>
          </div>
        </div>

        {/* MIDDLE: SOCIAL ACCOUNTS */}
        <div>
          <h2 className="text-2xl font-semibold mb-6 text-brand">Follow Us</h2>
          <p className="text-gray-300 text-sm leading-relaxed mb-6">
            Stay connected with us on social media for updates, tips, and new content.
          </p>
          <div className="flex items-center gap-4 mb-10">
            <a
              href="https://www.instagram.com/alnooredtech/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-12 h-12 rounded-full bg-gray-800 hover:bg-brand text-brand hover:text-gray-900 flex items-center justify-center transition-all duration-200"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://www.linkedin.com/company/alnooredtech/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="w-12 h-12 rounded-full bg-gray-800 hover:bg-brand text-brand hover:text-gray-900 flex items-center justify-center transition-all duration-200"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a
              href="https://web.facebook.com/profile.php?id=61580669345912"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="w-12 h-12 rounded-full bg-gray-800 hover:bg-brand text-brand hover:text-gray-900 flex items-center justify-center transition-all duration-200"
            >
              <Facebook className="w-5 h-5" />
            </a>
          </div>

          {/* BE A VOLUNTEER */}
          <div>
            <h2 className="text-2xl font-semibold mb-4 text-brand flex items-center gap-2">
              Be a Volunteer
            </h2>
            <p className="text-gray-300 mb-6 text-sm leading-relaxed">
              To help in our cause, Join Now!
            </p>
            <button
              onClick={() =>
                window.open(
                  "https://docs.google.com/forms/d/e/1FAIpQLSdm39y9venh3-JYho7xw2sMay9C8kGPYGCowg4s__Fisuk5UA/viewform",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              className="bg-brand text-gray-900 font-semibold px-6 py-2 rounded-md hover:bg-brand-dark transition-all duration-200 w-full sm:w-auto"
            >
              Join Now
            </button>
          </div>
        </div>

        {/* RIGHT SIDE: CONTACT FORM */}
        <div className="max-w-md">
          <h2 className="text-2xl font-semibold mb-6 text-brand">Get in Touch</h2>

          {submitted ? (
            <div className="py-10 text-center">
              <p className="text-green-400 font-semibold text-lg">Message received!</p>
              <p className="text-gray-400 text-sm mt-1">We'll get back to you soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <input type="text" name="name" placeholder="Name" value={form.name}
                onChange={handleChange} required
                className="w-full bg-gray-800 border border-gray-700 text-white text-sm p-2.5 rounded-md outline-none focus:ring-2 focus:ring-brand" />
              <input type="email" name="email" placeholder="Email" value={form.email}
                onChange={handleChange} required
                className="w-full bg-gray-800 border border-gray-700 text-white text-sm p-2.5 rounded-md outline-none focus:ring-2 focus:ring-brand" />
              <input type="text" name="phone" placeholder="Phone" value={form.phone}
                onChange={handleChange}
                className="w-full bg-gray-800 border border-gray-700 text-white text-sm p-2.5 rounded-md outline-none focus:ring-2 focus:ring-brand" />
              <textarea name="message" placeholder="Message" rows="3" value={form.message}
                onChange={handleChange} required
                className="w-full bg-gray-800 border border-gray-700 text-white text-sm p-2.5 rounded-md outline-none focus:ring-2 focus:ring-brand resize-none" />
              <button type="submit" disabled={submitting}
                className="bg-brand text-gray-900 font-semibold px-6 py-2 rounded-md hover:bg-brand-dark transition-all duration-200 disabled:opacity-50">
                {submitting ? "Sending..." : "Submit"}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* BOTTOM LINE */}
      <div className="border-t border-gray-700 mt-12 pt-6 text-center text-gray-500 text-sm">
        © {new Date().getFullYear()} Al Noor EdTech (Pvt.) Limited — All Rights Reserved.
      </div>
    </footer>
  );
};

export default Footer;
